import Button, { type ButtonProps } from '@pelckmans/elementis/components/button';
import ButtonGroup from '@pelckmans/elementis/components/button-group';
import DigibookIcon from '@pelckmans/elementis/icons/digibook';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../../constants/analyticsEvents';
import AccountContext from '../../../../../contexts/AccountContext';
import useAnalytics from '../../../../../hooks/useAnalytics';
import useStudioGroup from '../../../../../hooks/useStudioGroup';
import AppsContext from '../../../_contexts/AppsContext';
import ModuleContext from '../../../_contexts/ModuleContext';
import DigibookButton from '../../../components/DigibookButton';
import useDigibook from '../../../hooks/useDigibook';
import useSlideSets from '../../../hooks/useSlideSets';
import {
  buildCodexUrl,
  getDisabledTooltip,
  isDigibookApp,
  isOldDigibookApp,
  type DigibookApp,
} from '../../../utils/digibookAppUtils';
import AppQuickActionButton from './AppQuickActionButton';
import SlideSetAppButton from './SlideSetAppButton';
import WordListAppButton from './WordListAppButton';

function DigibookPageButton({
  digibookApp,
  pageNumber,
  size,
}: {
  digibookApp: DigibookApp;
  pageNumber: number;
  size: ButtonProps['size'];
}) {
  const { t } = useTranslation();
  const { account } = useContext(AccountContext);
  const { selectedModule } = useContext(ModuleContext);
  const { capture } = useAnalytics();
  const disabledTooltip = getDisabledTooltip(digibookApp);

  const href = digibookApp.linkedApp?.id
    ? buildCodexUrl({
        digibookId: digibookApp.linkedApp?.id,
        moduleId: digibookApp.moduleId,
        accountProvider: account?.provider,
        page: pageNumber,
      })
    : '';

  const subLocationId = selectedModule?.isExerciseModule
    ? ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_EXERCISE_MODULE_MATERIAL
    : ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MATERIAL;

  function captureOpenDigibookEvents() {
    capture({
      subLocationId,
      eventType: ANALYTICS_EVENT_TYPES.DIGIBOOK_ACCESSED,
      objectId: digibookApp.id.toString(),
    });
    capture({
      subLocationId,
      eventType: ANALYTICS_EVENT_TYPES.APP_ACCESSED,
      objectId: digibookApp.linkedApp?.id.toString(),
    });
  }

  return (
    <Button
      as="link"
      prominence="secondary"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      key="digibook-button"
      size={size}
      startIcon={<DigibookIcon data-testid="digibook-icon" />}
      disabled={!!disabledTooltip}
      tooltip={disabledTooltip ? t(disabledTooltip) : undefined}
      onClick={captureOpenDigibookEvents}
    >
      {pageNumber > 0
        ? t('PAGES.TOC_LEVEL.APPS.DIGIBOOK.OPEN_DIGIBOOK_WITH_PAGE', { page: pageNumber })
        : digibookApp.linkedApp?.manualIncluded
        ? t('PAGES.TOC_LEVEL.APPS.DIGIBOOK.OPEN_DIGIBOOK_WITH_MANUAL')
        : t('PAGES.TOC_LEVEL.APPS.DIGIBOOK.OPEN_DIGIBOOK')}
    </Button>
  );
}

export default function AppButtonList({
  density = 'normal',
  digibooksOnly = false,
  showDigibooks = true,
}: {
  density?: 'normal' | 'compact' | 'small';
  digibooksOnly?: boolean;
  showDigibooks?: boolean;
}) {
  const { module } = useContext(ModuleContext);
  const { apps } = useContext(AppsContext);

  const { t } = useTranslation();
  const { slideSets } = useSlideSets();
  const { digibook, pageNumber } = useDigibook();
  const studioGroup = useStudioGroup();

  const size = density === 'compact' ? 'sm' : 'md';

  if (!module) return null;

  const digibookApp = apps.find(app => isDigibookApp(app) && app.moduleId === module?.id);
  const oldDigibookApps = apps.filter(app => isOldDigibookApp(app) && app.moduleId === module?.id);

  if (!digibook && oldDigibookApps.length === 0 && apps.length === 0 && slideSets.length === 0) return null;

  const reverseOrder = showDigibooks && studioGroup === 'BAO';

  const digibookButtons = showDigibooks
    ? [
        ...(digibookApp
          ? [
              <DigibookPageButton
                key="digibook"
                digibookApp={digibookApp as DigibookApp}
                pageNumber={pageNumber}
                size={size}
              />,
            ]
          : []),
        <DigibookButton key="old-digibook" digibookApps={oldDigibookApps} size={size} />,
      ]
    : [];

  const appButtons = !digibooksOnly
    ? [
        <SlideSetAppButton key="presentations" size={size} />,
        <WordListAppButton key="word-lists" size={size} />,
        <AppQuickActionButton key="apps-quick-action" size={size} />,
      ]
    : [];

  const buttons = reverseOrder ? [...appButtons, ...digibookButtons] : [...digibookButtons, ...appButtons];

  return (
    <ButtonGroup role="group" aria-label={t('PAGES.TOC_LEVEL.APPS.A11Y_LABEL')} size={size}>
      {buttons}
    </ButtonGroup>
  );
}
